import React from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "../../Switch";
import Spinner from "../../Spinner";
import {
  AUDIO_ANALYSIS_AXIS_GRID_CLASS,
  formatSpectrogramZoomLabel,
  SPECTROGRAM_HEIGHT,
  SPECTROGRAM_MAX_ZOOM,
  SPECTROGRAM_MIN_ZOOM,
  SPECTROGRAM_WIDTH,
  SPECTROGRAM_ZOOM_STEP,
  WAVEFORM_HEIGHT,
} from "./audioAnalysis";
import { useAudioVisualisation } from "./useAudioVisualisation";

export default function SpectrogramPanel({
  title,
  subtitle,
  mediaRef,
  mediaUrl,
  loadingLabel,
  hintLabel,
  isChild = false,
}) {
  const { t } = useTranslation();
  const [useLogFrequencyScale, setUseLogFrequencyScale] = React.useState(false);
  const {
    analysisState,
    canvasRef,
    clampedViewStartTime,
    frequencyTicks,
    handleZoomChange,
    maxViewportStartTime,
    playheadRef,
    timeLabels,
    timeZoom,
    totalDuration,
    waveformCanvasRef,
    waveformPlayheadRef,
    waveformTicks,
    windowSliderDisabled,
    setViewStartTime,
  } = useAudioVisualisation({
    mediaRef,
    mediaUrl,
    t,
    useLogFrequencyScale,
  });

  if (analysisState === "unavailable") {
    return null;
  }

  const statusLabel =
    analysisState === "ready"
      ? hintLabel
      : loadingLabel;

  return (
    <div className={isChild ? "flex h-full flex-col" : "card p-4"}>
      {title ? (
        <h4 className="text-base font-semibold text-typography">{title}</h4>
      ) : null}
      {subtitle ? (
        <p className="mt-1 text-sm text-greyscale-3">{subtitle}</p>
      ) : null}

      <div className="mt-4 flex flex-col gap-8">
        <div className={AUDIO_ANALYSIS_AXIS_GRID_CLASS}>
          <div className="relative h-56 pr-1">
            <p className="absolute top-1/2 left-0 -translate-y-1/2 -rotate-90 whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide text-greyscale-3">
              {t(
                "profilePage.recordingViewerSpectrumFrequencyAxis",
                "Frequency",
              )}
            </p>
            <div className="relative h-full">
              {frequencyTicks.map((tick) => (
                <span
                  key={tick.label}
                  className="absolute right-0 -translate-y-1/2 text-[11px] text-greyscale-3"
                  style={{ top: tick.top }}
                >
                  {tick.label}
                </span>
              ))}
            </div>
          </div>

          <div
            className="relative overflow-hidden rounded-r-xl"
            aria-busy={analysisState === "loading"}
          >
            <canvas
              ref={canvasRef}
              width={SPECTROGRAM_WIDTH}
              height={SPECTROGRAM_HEIGHT}
              data-testid="recording-viewer-spectrogram"
              className="block h-56 w-full"
            />
            {analysisState === "loading" ? (
              <div
                data-testid="recording-viewer-spectrogram-spinner"
                aria-label={loadingLabel}
                className="absolute inset-0 flex items-center justify-center bg-background/75"
              >
                <div className="h-20 w-20">
                  <Spinner />
                </div>
              </div>
            ) : null}
            <div
              ref={playheadRef}
              className="pointer-events-none absolute inset-y-0 top-0 w-px bg-black/45 shadow-[0_0_0_1px_rgba(255,255,255,0.35)]"
              style={{ left: "0%" }}
            />
          </div>
        </div>

        <div className={AUDIO_ANALYSIS_AXIS_GRID_CLASS}>
          <div className="relative h-28 pr-1 pl-6">
            <p className="absolute top-1/2 left-0 -translate-y-1/2 -rotate-90 whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide text-greyscale-3">
              {t(
                "profilePage.recordingViewerWaveformAmplitudeAxis",
                "Amplitude",
              )}
            </p>
            <div className="relative h-full">
              {waveformTicks.map((tick) => (
                <span
                  key={tick.label}
                  className="absolute right-0 -translate-y-1/2 text-[11px] text-greyscale-3"
                  style={{ top: tick.top }}
                >
                  {tick.label}
                </span>
              ))}
            </div>
          </div>

          <div
            className="relative overflow-hidden rounded-r-xl bg-background"
            aria-busy={analysisState === "loading"}
          >
            <canvas
              ref={waveformCanvasRef}
              width={SPECTROGRAM_WIDTH}
              height={WAVEFORM_HEIGHT}
              data-testid="recording-viewer-waveform"
              className="block h-28 w-full"
            />
            <div
              ref={waveformPlayheadRef}
              className="pointer-events-none absolute inset-y-0 top-0 w-px bg-black/45 shadow-[0_0_0_1px_rgba(255,255,255,0.35)]"
              style={{ left: "0%" }}
            />
          </div>
        </div>

        <div className={AUDIO_ANALYSIS_AXIS_GRID_CLASS}>
          <div />
          <div>
            <div
              data-testid="recording-viewer-time-axis"
              className="flex items-center justify-between text-[11px] text-greyscale-3"
            >
              {timeLabels.map((tick, index) => (
                <span key={`${tick.label}-${index}`}>{tick.label}</span>
              ))}
            </div>

            {analysisState === "ready" && totalDuration > 0 ? (
              <div className="mt-3 flex w-full flex-wrap items-end justify-between gap-4">
                <Switch
                  checked={useLogFrequencyScale}
                  onChange={setUseLogFrequencyScale}
                  label={t(
                    "profilePage.recordingViewerSpectrumLogScaleLabel",
                    "Log scale",
                  )}
                />

                <div className="ml-auto flex flex-wrap justify-end gap-4">
                  <div className="flex max-w-64 flex-col gap-2">
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-xs font-medium text-typography">
                        {t(
                          "profilePage.recordingViewerSpectrumZoomLabel",
                          "Zoom",
                        )}
                      </span>
                      <span className="text-xs font-semibold text-typography">
                        {formatSpectrogramZoomLabel(timeZoom)}
                      </span>
                    </div>
                    <input
                      id="recording-viewer-spectrum-zoom"
                      data-testid="recording-viewer-spectrogram-zoom"
                      type="range"
                      min={SPECTROGRAM_MIN_ZOOM}
                      max={SPECTROGRAM_MAX_ZOOM}
                      step={SPECTROGRAM_ZOOM_STEP}
                      value={timeZoom}
                      onChange={(event) =>
                        handleZoomChange(Number(event.target.value) || 1)
                      }
                      className="w-full accent-primary"
                    />
                  </div>

                  <div className="flex max-w-64 flex-col gap-2">
                    <label
                      htmlFor="recording-viewer-spectrum-window"
                      className={`text-xs font-medium ${
                        windowSliderDisabled
                          ? "text-greyscale-3"
                          : "text-typography"
                      }`}
                    >
                      {t(
                        "profilePage.recordingViewerSpectrumWindowLabel",
                        "Window",
                      )}
                    </label>
                    <input
                      id="recording-viewer-spectrum-window"
                      data-testid="recording-viewer-spectrogram-window"
                      type="range"
                      min={0}
                      max={Math.max(maxViewportStartTime, 0)}
                      step={Math.max(totalDuration / SPECTROGRAM_WIDTH, 0.01)}
                      value={clampedViewStartTime}
                      disabled={windowSliderDisabled}
                      onChange={(event) =>
                        setViewStartTime(Number(event.target.value) || 0)
                      }
                      className={`w-full accent-primary ${
                        windowSliderDisabled
                          ? "cursor-not-allowed opacity-45"
                          : ""
                      }`}
                    />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {statusLabel ? (
        <p className="mt-3 text-xs text-greyscale-3">{statusLabel}</p>
      ) : null}
    </div>
  );
}
